import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  Validators,
} from "@angular/forms";
import { NgbDateParserFormatter } from "@ng-bootstrap/ng-bootstrap";
import { AuthService } from "app/auth/auth.service";
import { AlertController, NavParams } from "ionic-angular";
import { Payer, Plan } from "models/payers/payer";
import { Config } from "pages/patient-chart/patient-benefits/models";
import { commonVar } from "providers/common-var/common";
import { PayersService } from "providers/payers-service/payers.service";
import { StateManagementService } from "providers/state-management/state-management.service";
import { Subscription } from "rxjs";
import { BillingService } from "../../../providers/billing/billing-service";
import { UtilityService } from "../../../providers/utility-service/utility-service";
import { NgbCustomParserFormatter } from "../../common/date-picker/ngb-custom-parser-formatter";

// The payers OpenSearch index stores documents with dataType "PayerConfig"
// (see payers-view.ts and payer-functions constants.go DATA_TYPE_PAYER).
const PAYER_DATA_TYPE = "PayerConfig";

@Component({
  selector: "general-payer",
  templateUrl: "general-payer.html",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => GeneralPayer),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => GeneralPayer),
      multi: true,
    },
    { provide: NgbDateParserFormatter, useClass: NgbCustomParserFormatter },
  ],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class GeneralPayer implements OnInit, OnDestroy {
  innerPayerForm: FormGroup;
  _saveEnabled: boolean = false;
  subscriptions: Subscription[] = [];
  countyList: any[] = [];
  emailRegex = commonVar.EMAIL_REGEX;
  phoneMask = commonVar.PHONE_US_MASK;
  x = commonVar.ZIP_CODE_MASK;
  zipCodeExt = commonVar.ZIP_CODE_EXTENSION_MASK;
  stateList = commonVar.STATE_LIST;
  facilityTypes = commonVar.FACILITY_TYPES;
  alphaRegex = commonVar.ALPHA_REGEX;
  alphaNumericRegx = commonVar.ALPHANUMERIC;
  numberRegex = commonVar.NUMBER_REGEX;
  initialMedicareBenefitTypes: any;
  foundClaims: any = null;

  // CO-3392 (epic CO-2315): while OFF the payer form is unchanged; ON adds
  // the state-Medicaid classification fields (coverage type, state, parent
  // FFS payer, Medicare Advantage). Server-side validation in
  // payer-functions-save-payer is authoritative — these fields are UX.
  californiaMediCalSupportToggle: boolean = false;
  parentFfsCandidates: any[] = [];

  coverageTypeList: Config[] = [
    { name: "Fee-for-Service", value: "FFS", disabled: false },
    { name: "Managed Care", value: "MANAGED_CARE", disabled: false },
  ];

  @Input() isEditMode: boolean = false;
  @Input() currentPayer: any;
  @Input() showScheduledEligibility: any;

  // CO-2315 item #6 / F2.S14 — the elevated Agency Admin payer-classification permission.
  //
  // DEFAULTS TO FALSE, and that is the whole design. payer-functions refuses a
  // classification change with 403 unless the caller holds
  // Company:Payers:Classification:EDIT, and the form must never be MORE PERMISSIVE than
  // the API. Defaulting to true would leave the controls open while the permission ask is
  // in flight, and open forever if a parent forgot to pass the input — a fail-open UI in
  // front of a fail-closed API.
  //
  // The controls stay VISIBLE and become read-only, per F2.S14: hiding them would tell the
  // user nothing about why they cannot classify.
  private _canEditClassification = false;

  @Input()
  set canEditClassification(value: boolean) {
    this._canEditClassification = !!value;
    this.applyClassificationPermission();
  }

  get canEditClassification(): boolean {
    return this._canEditClassification;
  }

  /** The affordance naming the required permission — shown only where it is meaningful. */
  get showClassificationPermissionNotice(): boolean {
    return this.showMedicaidClassification && !this._canEditClassification;
  }

  /**
   * ALL FIVE controls, not just Coverage Type. The API gate refuses any change to the
   * classification set, so locking only the toggle would let a user edit Medicare
   * Advantage or the parent linkage and still meet a 403 they cannot explain.
   */
  private static readonly CLASSIFICATION_CONTROLS = [
    "coverageType",
    "classificationState",
    "parentStateMedicaidPayerId",
    "hospiceCarveOutToFfs",
    "isMedicareAdvantage",
  ];

  private applyClassificationPermission(): void {
    if (!this.innerPayerForm) {
      return;
    }
    GeneralPayer.CLASSIFICATION_CONTROLS.forEach((name) => {
      const control = this.innerPayerForm.get(name);
      if (!control) {
        return;
      }
      // emitEvent:false — the component subscribes to coverageType.valueChanges, and a
      // permission answer arriving must not look like the user changing the value.
      if (this._canEditClassification) {
        control.enable({ emitEvent: false });
      } else {
        control.disable({ emitEvent: false });
      }
    });
  }
  @Output() onTouched = new EventEmitter<boolean>();
  @Output() onPayerSaved = new EventEmitter<any>();
  @Output() onCanceled = new EventEmitter<any>();
  @Output() onBenefitTypeChanged = new EventEmitter<any>();
  @Output() onPayerTypeChanged = new EventEmitter<any>();
  @Output() onSetCheckmark = new EventEmitter<any>();

  payerTypeList: Config[] = [
    { name: "Commercial", value: "COMMERCIAL", disabled: false },
    { name: "Medicaid", value: "MEDICAID", disabled: false },
    { name: "Medicare", value: "MEDICARE", disabled: false },
  ];

  benefitsTypeList: Config[] = [
    { name: "Hospice", value: "HOSPICE", disabled: false },
    { name: "Palliative", value: "PALLIATIVE", disabled: false },
    { name: "Room And Board", value: "ROOMANDBOARD", disabled: false },
  ];

  set saveEnabled(value: boolean) {
    this._saveEnabled = value;
  }

  get saveEnabled() {
    return this._saveEnabled;
  }

  constructor(
    protected formBuilder: FormBuilder,
    protected alertController: AlertController,
    protected navParams: NavParams,
    private cd: ChangeDetectorRef,
    private authSvc: AuthService,
    private billingService: BillingService,
    private utilityService: UtilityService,
    private payersService: PayersService,
    private stateManagementService: StateManagementService
  ) {}

  ngOnInit(): void {
    this.mapPayerToFormGroup();
    this.medicarePayerValidations();
    this.subscriptions.push(
      this.stateManagementService.californiaMediCalSupportToggle.subscribe(
        (enabled) => {
          this.californiaMediCalSupportToggle = !!enabled;
          if (this.californiaMediCalSupportToggle) {
            this.loadParentFfsCandidates();
          }
          this.cd.markForCheck();
        }
      ),
      this.innerPayerForm
        .get("coverageType")
        .valueChanges.subscribe(() => this.coverageTypeChanged()),
      this.innerPayerForm.valueChanges.subscribe(this.onFormChanges),
      this.innerPayerForm.get("benefitType").valueChanges.subscribe((value) => {
        this.benefitTypeChanged(value);
      }),
      this.innerPayerForm.get("payerType").valueChanges.subscribe((value) => {
        this.payerTypeChanged(value);
      }),
      this.innerPayerForm
        .get("payToAddress")
        .valueChanges.subscribe((value) => {
          this.payToAddressChanged(value);
        }),
      this.innerPayerForm.get("address").valueChanges.subscribe(() => {
        this.planAddressChanged();
      }),
      this.innerPayerForm.get("city").valueChanges.subscribe(() => {
        this.planAddressChanged();
      }),
      this.innerPayerForm.get("state").valueChanges.subscribe(() => {
        this.planAddressChanged();
      }),
      this.innerPayerForm.get("zip").valueChanges.subscribe(() => {
        this.planAddressChanged();
      }),
      this.innerPayerForm.get("zipCode").valueChanges.subscribe(() => {
        this.planAddressChanged();
      })
    );
    this.initialMedicareBenefitTypes =
      this.innerPayerForm.get("benefitType").value;
    this.benefitTypeChanged(this.innerPayerForm.get("benefitType").value);
    this.payerTypeChanged(this.innerPayerForm.get("payerType").value);
    this.payToAddressChanged(this.innerPayerForm.get("payToAddress").value);
  }

  medicarePayerValidations() {
    if (
      this.currentPayer &&
      this.currentPayer.payerType.toUpperCase() == "MEDICARE"
    ) {
      this.innerPayerForm.get("payerType").disable();
    }
  }

  benefitTypeChanged(_value) {
    setTimeout(() => {
      this.innerPayerForm
        .get("benefitType")
        .setValue(this.innerPayerForm.get("benefitType").value.sort(), {
          emitEvent: false,
        });
      this.onBenefitTypeChanged.emit(
        this.innerPayerForm.get("benefitType").value
      );
    });
  }

  payerTypeChanged(value) {
    setTimeout(() => {
      if (value == "MEDICARE") {
        this.benefitsTypeList = [
          { name: "Hospice", value: "HOSPICE", disabled: false },
          { name: "Palliative", value: "PALLIATIVE", disabled: false },
        ];
        let benefitTypes = this.innerPayerForm.get("benefitType").value;
        if (benefitTypes.includes("ROOMANDBOARD")) {
          benefitTypes = benefitTypes.filter((item) => item !== "ROOMANDBOARD");
          this.innerPayerForm.get("benefitType").setValue(benefitTypes);
        }
      } else {
        this.benefitsTypeList = [
          { name: "Hospice", value: "HOSPICE", disabled: false },
          { name: "Palliative", value: "PALLIATIVE", disabled: false },
          { name: "Room And Board", value: "ROOMANDBOARD", disabled: false },
        ];
      }
      this.onPayerTypeChanged.emit(value);
      // CO-3882: payer type drives whether State is required (create + Medicaid).
      this.updateStateRequiredValidator();
    });
  }

  checkChar(event: any, type) {
    let newValue = event.target.value;
    if (type == "alpha") {
      let regExp = new RegExp("^[A-Za-z? ]+$");
      if (!regExp.test(newValue)) {
        event.target.value = newValue.replace(/[^A-Za-z ]/g, "");
      }
    } else if (type == "alphaNumeric") {
      let regExp = new RegExp("^[A-Za-z0-9? ]+$");
      if (!regExp.test(newValue)) {
        event.target.value = newValue.replace(/[^A-Za-z0-9 ]/g, "");
      }
    } else if (type == "numeric") {
      let regExp = new RegExp("^[0-9]+$");
      if (!regExp.test(newValue)) {
        event.target.value = newValue.replace(/[^0-9 ]/g, "");
      }
    }
  }

  valid(control: string) {
    return this.innerPayerForm.get(control).valid;
  }

  touched(control: string) {
    return this.innerPayerForm.get(control).touched;
  }

  validate(_: FormControl) {
    return this.innerPayerForm.valid
      ? null
      : { generalDetails: { valid: false } };
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  savePayer() {
    this.checkCanSave();
    if (this.saveEnabled) {
      let obj = this.mapPayerForm(this.innerPayerForm.controls);
      this.onPayerSaved.emit(obj);
      this.innerPayerForm.markAsPristine();
      this.saveEnabled = false;
    } else {
      this.showAlert({
        title: "Invalid Submission",
        message: "Missing required fields. Please review.",
      });
    }
  }

  cancel() {
    this.onCanceled.emit(true);
  }

  onFormChanges = (value) => {
    if (value) {
      this.checkCanSave();
      this.onTouched.emit(this.innerPayerForm.dirty);
    }
  };

  checkCanSave() {
    this.saveEnabled = this.innerPayerForm && this.innerPayerForm.valid;
  }

  mapPayerToFormGroup() {
    let payerName = "";
    let payerType = "";
    let benefitType = [];
    let address = "";
    let city = "";
    let state = "";
    let zip = "";
    let zipCode = "";
    let phoneNumber = "";
    let webAddress = "";
    let notes = "";
    let payToAddress = false;
    let payAddress = "";
    let payCity = "";
    let payState = "";
    let payZip = "";
    let payZipCode = "";
    let payPhoneNumber = "";
    let payWebAddress = "";
    let payNotes = "";
    let plans = "";
    let classificationState = "";
    let coverageType = "";
    let isMedicareAdvantage = false;
    let parentStateMedicaidPayerId = "";
    let hospiceCarveOutToFfs = false;

    if (this.currentPayer) {
      payerName = this.currentPayer.payerName;
      payerType = this.currentPayer.payerType.toUpperCase();
      let benefitList = this.benefitsTypeList.filter((x) =>
        this.currentPayer.benefitType.trim().split(",").includes(x.name)
      );
      if (benefitList.length > 0) {
        benefitList.forEach((element) => {
          const normalizedValue = this.payersService.normalizePayerDataForSave(
            element.value.toString()
          );
          if (
            this.currentPayer.payerType !== "MEDICARE" ||
            normalizedValue !== "ROOMANDBOARD"
          ) {
            benefitType.push(normalizedValue);
          }
        });
      }
      if (this.currentPayer.plans && this.currentPayer.plans.length > 0) {
        if (
          this.currentPayer.plans.filter(
            (x) => x.planName == "PlanAddress" || x.planName == "PayAddress"
          ).length == 0
        ) {
          let planAddress = this.currentPayer.plans[0];
          address = planAddress.address1;
          city = planAddress.city;
          state = planAddress.state;
          zip = planAddress.zipCode;
          zipCode = planAddress.zipCodeExtension;
          phoneNumber = planAddress.phoneNumber
            ? this.formatPhoneNumber(planAddress.phoneNumber)
            : "";
          webAddress = planAddress.webAddress;
          notes = planAddress.notes;
        } else {
          let planAddress = this.currentPayer.plans.filter(
            (x) => x.planName == "PlanAddress"
          )[0];
          if (planAddress) {
            address = planAddress.address1;
            city = planAddress.city;
            state = planAddress.state;
            zip = planAddress.zipCode;
            zipCode = planAddress.zipCodeExtension;
            phoneNumber = planAddress.phoneNumber
              ? this.formatPhoneNumber(planAddress.phoneNumber)
              : "";
            webAddress = planAddress.webAddress;
            notes = planAddress.notes;
          }
          let payAddressData = this.currentPayer.plans.filter(
            (x) => x.planName == "PayAddress"
          )[0];
          if (payAddressData) {
            payAddress = payAddressData.address1;
            payCity = payAddressData.city;
            payState = payAddressData.state;
            payZip = payAddressData.zipCode;
            payZipCode = payAddressData.zipCodeExtension;
            payPhoneNumber = payAddressData.phoneNumber
              ? this.formatPhoneNumber(payAddressData.phoneNumber)
              : "";
            payWebAddress = payAddressData.webAddress;
            payNotes = payAddressData.notes;
          }
        }
        payToAddress = this.currentPayer.useSameAddress
          ? this.currentPayer.useSameAddress
          : false;
        plans = this.currentPayer.plans;
      }
      classificationState = this.currentPayer.state || "";
      coverageType = this.currentPayer.isManagedCare
        ? "MANAGED_CARE"
        : this.currentPayer.state
        ? "FFS"
        : "";
      isMedicareAdvantage = this.currentPayer.isMedicareAdvantage || false;
      parentStateMedicaidPayerId =
        this.currentPayer.parentStateMedicaidPayerId || "";
      hospiceCarveOutToFfs = this.currentPayer.hospiceCarveOutToFfs || false;
    }

    this.innerPayerForm = this.formBuilder.group({
      payerName: [payerName, [Validators.required]],
      payerType: [payerType, [Validators.required]],
      benefitType: [benefitType, [Validators.required]],
      address: [address],
      city: [city, [Validators.pattern(this.alphaRegex)]],
      state: [state],
      zip: [
        zip,
        [Validators.pattern(this.numberRegex), Validators.minLength(5)],
      ],
      zipCode: [
        zipCode,
        [Validators.pattern(this.numberRegex), Validators.minLength(4)],
      ],
      phoneNumber: [phoneNumber, [Validators.minLength(13)]],
      webAddress: [webAddress, [Validators.maxLength(100)]],
      notes: [notes, [Validators.maxLength(255)]],
      payToAddress: [payToAddress],
      payAddress: [payAddress],
      payCity: [payCity, [Validators.pattern(this.alphaRegex)]],
      payState: [payState],
      payZip: [
        payZip,
        [Validators.pattern(this.numberRegex), Validators.minLength(5)],
      ],
      payZipCode: [
        payZipCode,
        [Validators.pattern(this.numberRegex), Validators.minLength(4)],
      ],
      payPhoneNumber: [payPhoneNumber, [Validators.minLength(13)]],
      payWebAddress: [payWebAddress, [Validators.maxLength(100)]],
      payNotes: [payNotes, [Validators.maxLength(255)]],
      plans: [plans],
      classificationState: [classificationState],
      coverageType: [coverageType],
      isMedicareAdvantage: [isMedicareAdvantage],
      parentStateMedicaidPayerId: [parentStateMedicaidPayerId],
      hospiceCarveOutToFfs: [hospiceCarveOutToFfs],
    });
    if (this.currentPayer) {
      let fullForm = true;
      for (const [key, value] of Object.entries(this.innerPayerForm.controls)) {
        if (value instanceof FormGroup || value instanceof FormArray) {
          continue;
        }
        if (
          key.toLowerCase() == "webaddress" ||
          key.toLowerCase() == "notes" ||
          key.toLowerCase() == "payaddress" ||
          key.toLowerCase() == "paycity" ||
          key.toLowerCase() == "paystate" ||
          key.toLowerCase() == "payzip" ||
          key.toLowerCase() == "payzipcode" ||
          key.toLowerCase() == "payphonenumber" ||
          key.toLowerCase() == "paywebaddress" ||
          key.toLowerCase() == "paynotes" ||
          key.toLowerCase() == "classificationstate" ||
          key.toLowerCase() == "coveragetype" ||
          key.toLowerCase() == "ismedicareadvantage" ||
          key.toLowerCase() == "parentstatemedicaidpayerid" ||
          key.toLowerCase() == "hospicecarveouttoffs"
        )
          continue;
        if (value.value == null || value.value === "") {
          fullForm = false;
          break;
        }
      }
      if (fullForm) {
        this.onSetCheckmark.emit();
      }
    }
    this.checkCanSave();
  

    // The form is rebuilt on payer load/change; without re-applying, a rebuilt form
    // comes back ENABLED and the gate is silently gone from the UI.
    this.applyClassificationPermission();
  }

  mapPayerForm(form: any) {
    if (form) {
      const p: Payer = {} as any as Payer;
      if (this.currentPayer != null) {
        p.payerId = this.currentPayer.payerId;
      }
      p.tenantId = this.authSvc.getUser().tenantId;
      p.payerName = form.payerName.value;
      p.payerType = form.payerType.value;
      p.benefitType = form.benefitType.value.join(",");
      p.plans = form.plans.value ? form.plans.value : [];
      if (
        p.plans &&
        p.plans.filter(
          (plan) =>
            plan.planName.toString() != "PlanAddress" ||
            plan.planName.toString() != "PayAddress"
        ).length > 0 &&
        p.plans.filter(
          (plan) =>
            plan.planName.toString() == "PlanAddress" ||
            plan.planName.toString() == "PayAddress"
        ).length == 0
      ) {
        p.plans.shift();
      }
      if (
        form.address ||
        form.city ||
        form.state ||
        form.zip ||
        form.webAddress ||
        form.notes ||
        form.phoneNumber
      ) {
        p.plans = p.plans.filter(
          (plan) => plan.planName.toString() != "PlanAddress"
        );
        const plan: Plan = {
          address1: form.address.value,
          city: form.city.value,
          planName: "PlanAddress",
          state: form.state.value,
          zipCode: form.zip.value,
          zipCodeExtension: form.zipCode.value,
          webAddress: form.webAddress.value,
          notes: form.notes.value,
          phoneNumber: this.formatPhoneNumberToBeSaved(form.phoneNumber.value),
        };
        p.plans.push(plan);
        p.payerAddress = this.formatAddressToShow(plan);
      }

      p.contactNumber = this.formatPhoneNumberToBeSaved(form.phoneNumber.value);

      p.useSameAddress = form.payToAddress.value
        ? form.payToAddress.value
        : false;

      if (
        form.payAddress ||
        form.payCity ||
        form.payState ||
        form.payZip ||
        form.payWebAddress ||
        form.payNotes ||
        form.payPhoneNumber
      ) {
        p.plans = p.plans.filter(
          (plan) => plan.planName.toString() != "PayAddress"
        );
        const plan: Plan = {
          address1: form.payAddress.value,
          city: form.payCity.value,
          planName: "PayAddress",
          state: form.payState.value,
          zipCode: form.payZip.value,
          zipCodeExtension: form.payZipCode.value,
          webAddress: form.payWebAddress.value,
          notes: form.payNotes.value,
          phoneNumber: this.formatPhoneNumberToBeSaved(
            form.payPhoneNumber.value
          ),
        };
        p.plans.push(plan);
        p.payToAddress = this.formatAddressToShow(plan);
      }
      if (this.showScheduledEligibility) {
        p.scheduleEligibility = this.currentPayer?.scheduleEligibility || false;
        p.scheduleFrequency = this.currentPayer?.scheduleFrequency || "";
        p.scheduleFrequencyUnit =
          this.currentPayer?.scheduleFrequencyUnit || "";
        p.scheduleUpdatedDate = this.currentPayer?.scheduleUpdatedDate || "";
        p.scheduleReceiverId = this.currentPayer?.scheduleReceiverId || "";
      }

      // CO-3882: State is governed by REQ-004, not the Foundation toggle, and its selector
      // now renders for every Medicaid payer — so persist it regardless of the toggle.
      // Previously this lived inside the toggle block, which silently dropped a typed State
      // on a toggle-off tenant ("the one that bites"). The Medi-Cal-SPECIFIC classification
      // fields stay toggle-gated.
      p.state = form.classificationState.value || "";
      if (this.californiaMediCalSupportToggle) {
        p.isManagedCare = form.coverageType.value === "MANAGED_CARE";
        p.isMedicareAdvantage = !!form.isMedicareAdvantage.value;
        p.parentStateMedicaidPayerId =
          form.parentStateMedicaidPayerId.value || null;
        p.hospiceCarveOutToFfs =
          form.coverageType.value === "MANAGED_CARE" &&
          !!form.hospiceCarveOutToFfs.value;
      }

      p.instantReceiverId = this.currentPayer?.instantReceiverId || "";
      p.instantEligibilityClearinghouseId =
        this.currentPayer?.instantEligibilityClearinghouseId ?? null;
      p.isActive = true;

      return p;
    }
    return;
  }

  showAlert = ({ title, message }) => {
    let alert = this.alertController.create({
      title,
      message,
      buttons: [
        {
          text: "Ok",
          handler: () => {},
        },
      ],
    });
    alert.present();
  };

  formatPhoneNumberToBeSaved(str) {
    return str.replace(/\D+/g, "");
  }

  formatPhoneNumber(phoneNumber) {
    if (phoneNumber.length === 10 && /^\d{10}$/.test(phoneNumber)) {
      const areaCode = phoneNumber.slice(0, 3);
      const firstPart = phoneNumber.slice(3, 6);
      const secondPart = phoneNumber.slice(6);
      phoneNumber = `(${areaCode})${firstPart}-${secondPart}`;
    }
    return phoneNumber;
  }

  formatAddressToShow(plan: Plan) {
    const addressComponents = [
      plan.address1 && plan.address1.trim(),
      plan.city && plan.city.trim(),
      plan.state && plan.state.trim(),
      plan.zipCode && plan.zipCode.trim(),
      plan.zipCodeExtension && plan.zipCodeExtension.trim(),
    ];

    return addressComponents
      .filter(Boolean)
      .join(", ")
      .replace(/, (\w+)/g, " $1");
  }

  payToAddressChanged(value) {
    if (value) {
      this.innerPayerForm.get("payState").disable();
    } else {
      this.innerPayerForm.get("payState").enable();
    }
    this.innerPayerForm
      .get("payAddress")
      .setValue(
        value
          ? this.innerPayerForm.get("address").value
          : this.getCurrentPayerPayAddress()
          ? this.getCurrentPayerPayAddress().address1
          : ""
      );
    this.innerPayerForm
      .get("payCity")
      .setValue(
        value
          ? this.innerPayerForm.get("city").value
          : this.getCurrentPayerPayAddress()
          ? this.getCurrentPayerPayAddress().city
          : ""
      );
    this.innerPayerForm
      .get("payState")
      .setValue(
        value
          ? this.innerPayerForm.get("state").value
          : this.getCurrentPayerPayAddress()
          ? this.getCurrentPayerPayAddress().state
          : ""
      );
    this.innerPayerForm
      .get("payZip")
      .setValue(
        value
          ? this.innerPayerForm.get("zip").value
          : this.getCurrentPayerPayAddress()
          ? this.getCurrentPayerPayAddress().zipCode
          : ""
      );
    this.innerPayerForm
      .get("payZipCode")
      .setValue(
        value
          ? this.innerPayerForm.get("zipCode").value
          : this.getCurrentPayerPayAddress()
          ? this.getCurrentPayerPayAddress().zipCodeExtension
          : ""
      );
  }

  planAddressChanged() {
    if (this.payToAddressValue()) {
      this.innerPayerForm
        .get("payAddress")
        .setValue(this.innerPayerForm.get("address").value);
      this.innerPayerForm
        .get("payCity")
        .setValue(this.innerPayerForm.get("city").value);
      this.innerPayerForm
        .get("payState")
        .setValue(this.innerPayerForm.get("state").value);
      this.innerPayerForm
        .get("payZip")
        .setValue(this.innerPayerForm.get("zip").value);
      this.innerPayerForm
        .get("payZipCode")
        .setValue(this.innerPayerForm.get("zipCode").value);
    }
  }

  getCurrentPayerPayAddress() {
    if (this.currentPayer && this.currentPayer.plans) {
      return (
        this.currentPayer.plans.find((x) => x.planName === "PayAddress") || null
      );
    }
    return null;
  }

  payToAddressValue() {
    return this.innerPayerForm.get("payToAddress").value;
  }

  async benefitTypeGotRemoved(removedOption) {
    let removedValue = removedOption.value.value.toUpperCase();
    if (this.currentPayer) {
      this.foundClaims = null;
      this.utilityService.increaseSpinnerCount(
        `Checking for ${removedValue} claims.`
      );
      await this.searchClaims(removedValue);
      if (this.foundClaims && this.foundClaims > 0) {
        let formValues = this.innerPayerForm.get("benefitType").value;
        formValues.push(removedValue);
        formValues.sort();
        this.innerPayerForm
          .get("benefitType")
          .setValue(formValues, { emitEvent: false });
      }
      this.utilityService.decreaseSpinnerCount();
    }
  }

  // CO-3392: convenience getters for the template.
  get showMedicaidClassification() {
    return (
      this.californiaMediCalSupportToggle &&
      this.innerPayerForm &&
      this.innerPayerForm.get("payerType").value === "MEDICAID"
    );
  }

  // CO-3882 (F2.S12): the Medicaid State field is governed by REQ-004, NOT the Medi-Cal
  // Payer Foundation toggle — it renders and saves for ANY Medicaid payer whatever the flag
  // says. (The Medi-Cal-specific controls — Coverage Type, parent FFS, hospice carve-out,
  // Medicare Advantage — stay behind showMedicaidClassification.)
  get showMedicaidState() {
    return (
      this.innerPayerForm &&
      this.innerPayerForm.get("payerType").value === "MEDICAID"
    );
  }

  // CO-3882 (F2.S6): "State required" narrows to CREATE. Editing an existing Medicaid payer
  // that already has no State must not be blocked (F2.S10 — protects the 828 legacy records).
  get stateRequired() {
    return !this.currentPayer && this.showMedicaidState;
  }

  get showMedicareAdvantage() {
    return (
      this.californiaMediCalSupportToggle &&
      this.innerPayerForm &&
      this.innerPayerForm.get("payerType").value === "MEDICARE"
    );
  }

  get isManagedCareSelected() {
    return (
      this.innerPayerForm &&
      this.innerPayerForm.get("coverageType").value === "MANAGED_CARE"
    );
  }

  coverageTypeChanged() {
    // CO-3882: State-required no longer keys on Coverage Type (that control is toggle-gated
    // and off-screen when the Foundation is off). It is create-scoped now — see
    // updateStateRequiredValidator — so just re-evaluate it here.
    this.updateStateRequiredValidator();
    if (!this.isManagedCareSelected) {
      this.innerPayerForm
        .get("parentStateMedicaidPayerId")
        .setValue("", { emitEvent: false });
    }
    this.checkCanSave();
  }

  // CO-3882 (F2.S6): State is required only when CREATING a Medicaid payer — not on edit
  // (F2.S10 keeps the 828 legacy no-State payers saveable) and not tied to Coverage Type or
  // the Foundation toggle. Keyed on stateRequired (!currentPayer && Medicaid payer type).
  private updateStateRequiredValidator() {
    const stateControl = this.innerPayerForm.get("classificationState");
    if (!stateControl) {
      return;
    }
    if (this.stateRequired) {
      stateControl.setValidators([Validators.required]);
    } else {
      stateControl.clearValidators();
    }
    stateControl.updateValueAndValidity({ emitEvent: false });
  }

  // CO-3663: the parent FFS dropdown must list only FFS payers in the selected
  // Medicaid State (the data holds every state's FFS payer). Filter the
  // already-fetched candidates by classificationState via a template getter, so
  // no refetch is needed. Empty when no FFS payer exists for the state.
  get filteredParentFfsCandidates(): any[] {
    const selectedState =
      this.innerPayerForm &&
      this.innerPayerForm.get("classificationState") &&
      this.innerPayerForm.get("classificationState").value;
    if (!selectedState) return [];
    const state = String(selectedState).toUpperCase().trim();
    return (this.parentFfsCandidates || []).filter(
      (c) => String(c.state).toUpperCase().trim() === state
    );
  }

  classificationStateChanged() {
    // A parent FFS payer is state-specific; clear any stale (possibly
    // out-of-state) selection when the Medicaid State changes so a selection
    // cannot survive a state change (CO-3663).
    this.innerPayerForm
      .get("parentStateMedicaidPayerId")
      .setValue("", { emitEvent: false });
    this.checkCanSave();
  }

  // Parent candidates = the tenant's active Medicaid FFS payers saved with
  // the new model (state set, not managed care). Server-side validation in
  // payer-functions-save-payer re-checks the reference authoritatively.
  loadParentFfsCandidates() {
    const searchDetails: any = {
      dataType: PAYER_DATA_TYPE,
      mustFields: [
        { name: "dataType", value: PAYER_DATA_TYPE },
        { name: "payerType", value: "MEDICAID" },
      ],
      optionalFields: [],
      from: 0,
      size: 500,
    };
    this.payersService.searchPayersFromOS(searchDetails).subscribe(
      (response) => {
        const hits = (response && response["hits"]) || [];
        this.parentFfsCandidates = hits
          .filter(
            (h) =>
              h.state &&
              !h.isManagedCare &&
              h.status !== "DELETED" &&
              h.status !== "ARCHIVED" &&
              (!this.currentPayer || h.payerId !== this.currentPayer.payerId)
          )
          .map((h) => ({
            payerId: h.payerId,
            name: `${h.payerName} (${h.state})`,
            state: h.state,
          }));
        this.cd.markForCheck();
      },
      () => {
        this.parentFfsCandidates = [];
      }
    );
  }

  async searchClaims(benefitType) {
    return new Promise((resolve, reject) => {
      let reqBody = this.createSearchClaimsRequestBody(
        benefitType.toString(),
        this.currentPayer.tenantId.toString(),
        this.currentPayer.payerId.toString()
      );
      this.billingService.searchFromClaimFunctions(reqBody).subscribe(
        (response) => {
          if (response !== null && response["hits"] !== null) {
            this.foundClaims = response["totalHits"];
          } else {
            this.foundClaims = null;
          }
          resolve();
        },
        (err) => {
          this.foundClaims = null;
          reject();
        }
      );
    });
  }

  createSearchClaimsRequestBody(benefitType, tenantId, payerId) {
    let details: any = {};
    details.dataType = "CLAIM";
    details.mustFields = [
      {
        name: "dataType",
        value: "CLAIM",
      },
      {
        name: "tenantId",
        value: tenantId,
      },
      {
        name: "payerId",
        value: payerId,
      },
      {
        name: "claimType",
        value: benefitType.toUpperCase(),
      },
    ];
    details.optionalFields = [];
    details.dateRange = null;
    details.searchString = null;
    details.from = 0;
    details.size = 10;
    details.sortBy = {
      name: "id",
      value: "DESC",
    };
    details.claimIdOnly = true;
    return details;
  }
}



// WEBPACK FOOTER //
// ./src/components/payers/general-payer/general-payer.ts